import { useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { RootState } from "../store";
import { useApp } from "../contexts/AppContext";
import { useLanguage } from "../contexts/LanguageContext";
import { TaskInstance, RepeatPattern, User } from "../types";
import { 
  useUpdateProjectTaskMutation, 
  useDeleteProjectTaskMutation,
  useCompleteProjectTaskMutation 
} from "../store/api/tasks";
import { useGetProjectsQuery } from "../store/api/projects";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "./ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "./ui/alert-dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { Checkbox } from "./ui/checkbox";
import {
  FileText,
  Users,
  Clock,
  CheckCircle2,
  Repeat,
  Edit,
  X,
  Loader2,
  Calendar as CalendarIcon,
} from "lucide-react";
import { format } from "date-fns";
import { ja, enUS } from "date-fns/locale";
import { Calendar } from "./ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { isOverdue, getOverdueDays } from "../utils/taskGenerator";
import { parseApiDate } from "../utils/date";
import { toast } from "sonner";
import { handleApiError } from "../utils/apiErrors";
import CompleteTaskConfirmDialog from "./CompleteTaskConfirmDialog";
import UncompleteTaskConfirmDialog from "./UncompleteTaskConfirmDialog";
import { WORKSPACE_DEFAULT_TIMEZONE } from "../utils/constants";

interface TaskDetailModalProps {
  taskId: string;
  onClose: () => void;
  // Optional props for API data (when task is not in context)
  task?: TaskInstance;
  users?: User[];
  projectMembers?: User[];
  onTaskUpdate?: () => void;  // Optional: callback when task is updated
}

// 繰り返しパターンの説明を取得
function getRepeatPatternDescription(pattern: RepeatPattern, language: 'ja' | 'en' = 'ja'): string {
  const { type } = pattern;
  
  if (language === 'ja') {
    if (type === 'daily') {
      return '毎日';
    } else if (type === 'weekly') {
      return '毎週';
    } else if (type === 'monthly') {
      return '毎月';
    } else if (type === 'quarterly') {
      return '四半期ごと';
    } else if (type === 'semi-annually') {
      return '半年ごと';
    } else if (type === 'yearly') {
      return '毎年';
    } else if (type === 'none') {
      return '繰り返しなし';
    }
  } else {
    if (type === 'daily') {
      return 'Daily';
    } else if (type === 'weekly') {
      return 'Weekly';
    } else if (type === 'monthly') {
      return 'Monthly';
    } else if (type === 'quarterly') {
      return 'Quarterly';
    } else if (type === 'semi-annually') {
      return 'Semi-annually';
    } else if (type === 'yearly') {
      return 'Yearly';
    } else if (type === 'none') {
      return 'No recurrence';
    }
  }
  
  return type;
}

export default function TaskDetailModal({ 
  taskId, 
  onClose,
  task: propTask,
  users: propUsers,
  projectMembers,
  onTaskUpdate,
}: TaskDetailModalProps) {
  const appContext = useApp();
  const dialogContentRef = useRef<HTMLDivElement>(null);
  const {
    taskInstances,
    routineTasks,
    projects: contextProjects,
    users: contextUsers,
    completeTask: contextCompleteTask,
    uncompleteTask: contextUncompleteTask,
    deleteTaskInstance: contextDeleteTaskInstance,
    updateTaskInstance: contextUpdateTaskInstance,
    currentUser,
  } = appContext;
  
  // Get workspace from Redux
  const currentWorkspace = useSelector((state: RootState) => state.workspace.currentWorkspace);
  const workspaceId = currentWorkspace?.id || '';
  const workspaceTimeZone = currentWorkspace?.timezone || WORKSPACE_DEFAULT_TIMEZONE;
  
  // Fetch projects from API
  const { data: apiProjects = [] } = useGetProjectsQuery(
    workspaceId,
    { skip: !workspaceId }
  );
  
  // API mutations
  const [updateProjectTask, { isLoading: isUpdating }] = useUpdateProjectTaskMutation();
  const [deleteProjectTask, { isLoading: isDeleting }] = useDeleteProjectTaskMutation();
  const [completeProjectTask, { isLoading: isCompleting }] = useCompleteProjectTaskMutation();
  
  const { language, t } = useLanguage();

  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [confirmCompleteTask, setConfirmCompleteTask] = useState(false);
  const [confirmUncompleteTask, setConfirmUncompleteTask] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState("");
  const [editDescription, setEditDescription] = useState("");
  const [editDueDate, setEditDueDate] = useState<Date | undefined>(undefined);
  const [editAssigneeIds, setEditAssigneeIds] = useState<string[]>([]);
  const [editErrors, setEditErrors] = useState<{name?: string; description?: string}>({});

  // Use provided task or look up from context
  const task = propTask || taskInstances.find((t) => t.id === taskId);
  if (!task) return null;

  // Determine if we should use API (task from API won't be in context)
  const useApi = !!propTask && !!workspaceId;
  
  // Use provided users or context users
  const users = propUsers || contextUsers;
  
  // Use project members for assignee selection, fallback to all users
  const assignableUsers = projectMembers || users;
  
  // Use API projects or context projects
  const projects = apiProjects.length > 0 ? apiProjects : contextProjects;

  // Get routine task and project
  const routineTask = routineTasks.find((rt) => rt.id === task.routineTaskId);
  const project = projects.find((p) => p.id === task.projectId);
  
  // Get repeat pattern from task directly (API) or from routineTasks (context)
  const taskRepeatPattern = (task as any).repeatPattern;
  const repeatPattern = taskRepeatPattern || routineTask?.repeatPattern;
  
  const assignees = users.filter((u) => task.assigneeIds.includes(u.id));
  
  const isLoading = isUpdating || isDeleting || isCompleting;

  const overdueDays = getOverdueDays(task, workspaceTimeZone);

  // 編集モードに入る時に現在の値を設定
  const handleStartEdit = () => {
    setEditName(task.name);
    setEditDescription(task.description || "");
    const dueDate = task.dueDate instanceof Date ? task.dueDate : parseApiDate(task.dueDate);
    setEditDueDate(dueDate);
    setEditAssigneeIds(task.assigneeIds);
    setEditErrors({});
    setIsEditing(true);
  };

  // 編集をキャンセル
  const handleCancelEdit = () => {
    setIsEditing(false);
  };

  // 編集を保存
  const handleSaveEdit = async () => {
    // Validate form
    const newErrors: {name?: string; description?: string} = {};

    if (!editName.trim()) {
      newErrors.name = t('tasks.taskNameRequiredError');
    } else if (editName.length > 50) {
      newErrors.name = t('tasks.taskNameMaxLength');
    }

    if (editDescription && editDescription.length > 150) {
      newErrors.description = t('tasks.taskDescMaxLength');
    }

    setEditErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      return;
    }

    if (!editDueDate) {
      toast.error(language === 'ja' ? '期限日を選択してください' : 'Please select a due date');
      return;
    }

    if (useApi) {
      try {
        await updateProjectTask({
          workspaceId,
          projectId: task.projectId,
          taskId: task.id,
          data: {
            name: editName,
            description: editDescription,
            dueDate: format(editDueDate, "yyyy-MM-dd"),
            assigneeIds: editAssigneeIds,
          },
        }).unwrap();
        toast.success(t('tasks.taskUpdated'));
        setIsEditing(false);
        onTaskUpdate?.();
        onClose();
      } catch (error) {
        console.error('Failed to update task:', error);
        handleApiError(error, t('tasks.taskUpdateFailed') || 'Failed to update task');
      }
    } else {
      contextUpdateTaskInstance(task.id, {
        name: editName,
        description: editDescription,
        dueDate: editDueDate,
        assigneeIds: editAssigneeIds,
      });
      toast.success(t('tasks.taskUpdated'));
      setIsEditing(false);
    }
  };

  const handleToggleComplete = async () => {
    if (task.status === "completed") {
      // Show confirmation dialog before uncompleting
      setConfirmUncompleteTask(true);
    } else {
      // Show confirmation dialog before completing
      setConfirmCompleteTask(true);
    }
  };

  const handleCompleteTask = async () => {
    if (useApi) {
      try {
        const result = await completeProjectTask({
          workspaceId,
          projectId: task.projectId,
          taskId: task.id,
          status: 'completed',
        }).unwrap();
        
        if (result.nextTask) {
          toast.success(t('tasks.taskCompletedWithNext') || 'Task completed! Next occurrence created.');
        } else {
          toast.success(t('tasks.taskCompleted') || 'Task completed');
        }
        onTaskUpdate?.();
        onClose();
      } catch (error) {
        console.error('Failed to complete task:', error);
        handleApiError(error, t('tasks.taskCompleteFailed') || 'Failed to complete task');
      }
    } else {
      if (currentUser) {
        contextCompleteTask(task.id, currentUser.id);
      }
    }
  };

  const handleUncompleteTask = async () => {
    if (useApi) {
      try {
        await completeProjectTask({
          workspaceId,
          projectId: task.projectId,
          taskId: task.id,
          status: 'pending',
        }).unwrap();
        toast.success(t('tasks.taskMarkedPending') || 'Task marked as pending');
        onTaskUpdate?.();
        onClose();
      } catch (error) {
        console.error('Failed to uncomplete task:', error);
        handleApiError(error, t('tasks.taskUncompleteFailed') || 'Failed to uncomplete task');
      }
    } else {
      contextUncompleteTask(task.id);
      onClose();
    }
  };

  const handleDeleteTask = async () => {
    if (useApi) {
      try {
        await deleteProjectTask({
          workspaceId,
          projectId: task.projectId,
          taskId: task.id,
        }).unwrap();
        toast.success(t('tasks.taskDeleted'));
        onTaskUpdate?.();
        onClose();
      } catch (error) {
        console.error('Failed to delete task:', error);
        handleApiError(error, t('tasks.taskDeleteFailed') || 'Failed to delete task');
      }
    } else {
      contextDeleteTaskInstance(task.id);
      toast.success(t('tasks.taskDeleted'));
      onClose();
    }
  };

  const handleToggleAssignee = (userId: string) => {
    setEditAssigneeIds((prev) =>
      prev.includes(userId)
        ? prev.filter((id) => id !== userId)
        : [...prev, userId]
    );
  };

  const locale = language === "ja" ? ja : enUS;

  // Scroll to top when modal opens or enters edit mode
  useEffect(() => {
    if (dialogContentRef.current) {
      dialogContentRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, []);

  useEffect(() => {
    if (isEditing && dialogContentRef.current) {
      // Scroll to top when entering edit mode
      setTimeout(() => {
        if (dialogContentRef.current) {
          dialogContentRef.current.scrollTo({ top: 0, behavior: 'smooth' });
        }
      }, 100);
    }
  }, [isEditing]);

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent ref={dialogContentRef} className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-gray-900">{t('tasks.taskDetails')}</DialogTitle>
          <DialogDescription>
            {t('tasks.taskDetailDescription')}
          </DialogDescription>
        </DialogHeader>

        {isEditing ? (
          /* 編集モード */
          <div className="space-y-6 pt-4">
            {/* タスク名 */}
            <div className="space-y-2">
              <Label className="mb-2" htmlFor="edit-name">{t('tasks.taskName')}</Label>
              <Input
                id="edit-name"
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                placeholder={t('tasks.taskNamePlaceholder')}
                maxLength={50}
                className={editErrors.name ? "border-red-500" : ""}
              />
              {editErrors.name && (
                <p className="mt-1 text-sm text-red-500">{editErrors.name}</p>
              )}
            </div>

            {/* 説明 */}
            <div className="space-y-2">
              <Label className="mb-2" htmlFor="edit-description">{t('common.description')}</Label>
              <Textarea
                id="edit-description"
                value={editDescription}
                onChange={(e) => setEditDescription(e.target.value)}
                placeholder={t('tasks.taskDescPlaceholder')}
                rows={3}
                maxLength={150}
                className={editErrors.description ? "border-red-500" : ""}
              />
              {editErrors.description && (
                <p className="mt-1 text-sm text-red-500">{editErrors.description}</p>
              )}
            </div>

            {/* 期限日 */}
            <div className="space-y-2">
              <Label>{t('tasks.dueDate')} *</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className="w-full justify-start text-left font-normal"
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {editDueDate ? (
                      format(editDueDate, "PPP", { locale: language === 'ja' ? ja : enUS })
                    ) : (
                      <span className="text-gray-500">
                        {language === 'ja' ? '日付を選択' : 'Select date'}
                      </span>
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={editDueDate}
                    onSelect={setEditDueDate}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>

            {/* 担当者選択 */}
            <div className="space-y-2">
              <Label>{t('tasks.assignees')}</Label>
              <div className="border border-gray-200 rounded-lg p-3 max-h-48 overflow-y-auto space-y-2">
                {assignableUsers.map((user) => (
                  <div
                    key={user.id}
                    className="flex items-center gap-3 p-2"
                  >
                    <Checkbox
                      id={`assignee-${user.id}`}
                      checked={editAssigneeIds.includes(user.id)}
                      onCheckedChange={(checked) => {
                        if (checked) {
                          setEditAssigneeIds([...editAssigneeIds, user.id]);
                        } else {
                          setEditAssigneeIds(editAssigneeIds.filter((id) => id !== user.id));
                        }
                      }}
                    />
                    <label
                      htmlFor={`assignee-${user.id}`}
                      className="flex items-center gap-3 cursor-pointer flex-1"
                    >
                      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-400 to-purple-500 flex items-center justify-center text-white">
                        {user.name.charAt(0)}
                      </div>
                      <div className="flex-1">
                        <p className="text-gray-900">{user.name}</p>
                        <p className="text-xs text-gray-500">{user.email}</p>
                      </div>
                    </label>
                  </div>
                ))}
              </div>
            </div>

            {/* プロジェクト情報（読み取り専用） */}
            {project && (
              <div className="p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <FileText className="w-4 h-4 text-gray-600" />
                  <span className="text-gray-700">{t('tasks.project')}{language === 'ja' ? '（変更不可）' : ' (Read-only)'}</span>
                </div>
                <p className="text-gray-900">{project.name}</p>
              </div>
            )}

            {/* 繰り返しパターン（読み取り専用） */}
            {repeatPattern && repeatPattern.type !== 'none' && (
              <div className="p-4 bg-blue-50 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <Repeat className="w-4 h-4 text-blue-600" />
                  <span className="text-blue-700">{t('tasks.repeatPattern')}{language === 'ja' ? '（変更不可）' : ' (Read-only)'}</span>
                </div>
                <p className="text-blue-900">
                  {getRepeatPatternDescription(repeatPattern, language)}
                </p>
              </div>
            )}

            {/* 編集ボタン */}
            <div className="flex items-center gap-3 pt-4 border-t">
              <Button
                onClick={handleSaveEdit}
                className="flex-1"
                disabled={isUpdating}
              >
                {isUpdating && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                {t('common.save')}
              </Button>
              <Button
                onClick={handleCancelEdit}
                variant="outline"
                className="flex-1"
                disabled={isUpdating}
              >
                <X className="w-4 h-4 mr-2" />
                {t('common.cancel')}
              </Button>
            </div>
          </div>
        ) : (
          /* 表示モード */
          <div className="space-y-6 pt-4">
            {/* タスク名 */}
            <div>
              <h3 className="text-gray-900 mb-2">{task.name}</h3>
              {task.description && (
                <p className="text-gray-600">{task.description}</p>
              )}
            </div>

            {/* ステータス */}
            <div className="flex items-center gap-3">
                <div
                  className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full ${
                    task.status === "completed"
                      ? "bg-green-100 text-green-700"
                      : isOverdue(task, workspaceTimeZone)
                      ? "bg-red-100 text-red-700"
                      : "bg-yellow-100 text-yellow-700"
                  }`}
                >
                <CheckCircle2 className="w-4 h-4" />
                <span>
                  {task.status === "completed"
                    ? t('tasks.completed')
                    : isOverdue(task, workspaceTimeZone)
                    ? t('tasks.overdueDays').replace('%{days}', String(overdueDays))
                    : t('tasks.notCompleted')}
                </span>
              </div>
            </div>

            {/* プロジェクト情報 */}
            {project && (
              <div className="p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <FileText className="w-4 h-4 text-gray-600" />
                  <span className="text-gray-700">{t('tasks.project')}</span>
                </div>
                <p className="text-gray-900">{project.name}</p>
                {project.description && (
                  <p className="text-gray-600 text-sm mt-1">{project.description}</p>
                )}
              </div>
            )}

            {/* 繰り返しパターン */}
            {repeatPattern && repeatPattern.type !== 'none' && (
              <div className="p-4 bg-blue-50 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <Repeat className="w-4 h-4 text-blue-600" />
                  <span className="text-blue-700">{t('tasks.repeatPattern')}</span>
                </div>
                <p className="text-blue-900">
                  {getRepeatPatternDescription(repeatPattern, language)}
                </p>
                <p className="text-blue-700 text-sm mt-2">
                  {t('tasks.autoGenerateNextTask')}
                </p>
              </div>
            )}

            {/* 日付情報 */}
            <div className="grid grid-cols-2 gap-4">
              {task.occurrenceDate && (
                <div className="p-4 border border-gray-200 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <Clock className="w-4 h-4 text-gray-600" />
                    <span className="text-gray-700">{t('tasks.occurrenceDate')}</span>
                  </div>
                  <p className="text-gray-900">
                    {format(
                      task.occurrenceDate instanceof Date ? task.occurrenceDate : new Date(task.occurrenceDate), 
                      language === 'ja' ? "yyyy年M月d日(E)" : "MMM d, yyyy (E)", 
                      { locale }
                    )}
                  </p>
                </div>
              )}

              {task.dueDate && (
                <div
                  className={`p-4 border rounded-lg ${
                    isOverdue(task, workspaceTimeZone)
                      ? "border-red-200 bg-red-50"
                      : "border-gray-200"
                  }`}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <Clock className={`w-4 h-4 ${isOverdue(task, workspaceTimeZone) ? "text-red-600" : "text-gray-600"}`} />
                    <span className={isOverdue(task, workspaceTimeZone) ? "text-red-700" : "text-gray-700"}>
                      {t('tasks.dueDate')}
                    </span>
                  </div>
                  <p className={isOverdue(task, workspaceTimeZone) ? "text-red-900" : "text-gray-900"}>
                    {format(
                      task.dueDate instanceof Date ? task.dueDate : new Date(task.dueDate), 
                      language === 'ja' ? "yyyy年M月d日(E)" : "MMM d, yyyy (E)", 
                      { locale }
                    )}
                  </p>
                </div>
              )}
            </div>

            {/* 担当者 */}
            {assignees.length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <Users className="w-4 h-4 text-gray-600" />
                  <span className="text-gray-700">{t('tasks.assignees')}</span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {assignees.map((user) => (
                    <div
                      key={user.id}
                      className="flex items-center gap-2 px-3 py-2 bg-gray-100 rounded-lg"
                    >
                      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-400 to-purple-500 flex items-center justify-center text-white">
                        {user.name.charAt(0)}
                      </div>
                      <div>
                        <p className="text-gray-900">{user.name}</p>
                        <p className="text-gray-600 text-xs">{user.email}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* 完了情報 */}
            {task.status === "completed" && task.completedAt && task.completerId && (
              <div className="p-4 bg-green-50 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <CheckCircle2 className="w-4 h-4 text-green-600" />
                  <span className="text-green-700">{t('tasks.taskCompletionInfo')}</span>
                </div>
                <p className="text-green-900">
                  {format(
                    task.completedAt instanceof Date ? task.completedAt : new Date(task.completedAt), 
                    language === 'ja' ? "yyyy年M月d日(E) HH:mm" : "MMM d, yyyy (E) HH:mm", 
                    { locale }
                  )}
                </p>
                <p className="text-green-700 text-sm mt-1">
                  {t('tasks.completedBy')}: {users.find((u) => u.id === task.completerId)?.name}
                </p>
              </div>
            )}
          </div>
        )}

        {!isEditing && (
          <DialogFooter className="flex justify-between items-center">
            <Button
              onClick={() => setIsDeleteDialogOpen(true)}
              variant="destructive"
              size="sm"
              disabled={isLoading}
            >
              {isDeleting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {t('tasks.deleteTaskButton')}
            </Button>
            <div className="flex items-center gap-2">
              <Button
                onClick={handleStartEdit}
                variant="outline"
                size="sm"
                className="flex items-center gap-2"
                disabled={isLoading}
              >
                <Edit className="w-4 h-4" />
                {t('common.edit')}
              </Button>
              <Button
                onClick={handleToggleComplete}
                variant={task.status === "completed" ? "outline" : "default"}
                size="sm"
                disabled={isLoading}
              >
                {isCompleting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                {task.status === "completed" ? t('tasks.toggleComplete') : t('tasks.markAsIncomplete')}
              </Button>
            </div>
          </DialogFooter>
        )}
      </DialogContent>

      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('tasks.deleteTaskConfirm')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('tasks.deleteTaskConfirmDesc')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteTask}>{t('common.delete')}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <CompleteTaskConfirmDialog
        open={confirmCompleteTask}
        onOpenChange={(open) => {
          if (!open) {
            setConfirmCompleteTask(false);
          }
        }}
        task={task}
        onComplete={() => {
          handleCompleteTask();
          setConfirmCompleteTask(false);
        }}
      />

      <UncompleteTaskConfirmDialog
        open={confirmUncompleteTask}
        onOpenChange={(open) => {
          if (!open) {
            setConfirmUncompleteTask(false);
          }
        }}
        task={task}
        onUncomplete={() => {
          handleUncompleteTask();
          setConfirmUncompleteTask(false);
        }}
      />
    </Dialog>
  );
}
